import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Alert } from '@/client/components/ui/alert';
import { api } from '@/client/lib/api';
import type { PlaybackTokenResponse } from '@/client/types/courses';
import { Watermark } from './Watermark';

type Props = {
  episodeId: string;
  /** Used to throttle progress reporting + drive course-completion checks. */
  videoDurationSeconds: number | null;
  onProgress?: (watchedSeconds: number) => void;
  onCompleted?: () => void;
};

const REFETCH_BEFORE_EXPIRY_SECONDS = 60;
const PROGRESS_REPORT_INTERVAL_SECONDS = 10;
const STREAM_SDK_SRC = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
const STREAM_SDK_ID = 'cloudflare-stream-sdk';

type StreamPlayerSdk = {
  Stream: (iframe: HTMLIFrameElement) => StreamPlayer;
};

type StreamPlayer = {
  addEventListener: (event: string, handler: () => void) => void;
  removeEventListener: (event: string, handler: () => void) => void;
  currentTime: number;
};

declare global {
  interface Window {
    Stream?: StreamPlayerSdk['Stream'];
  }
}

function loadStreamSdk(): Promise<StreamPlayerSdk['Stream']> {
  if (typeof window !== 'undefined' && window.Stream) {
    return Promise.resolve(window.Stream);
  }
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(STREAM_SDK_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.Stream) resolve(window.Stream);
        else reject(new Error('Stream SDK loaded but window.Stream missing'));
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load Stream SDK')));
      return;
    }
    const script = document.createElement('script');
    script.id = STREAM_SDK_ID;
    script.src = STREAM_SDK_SRC;
    script.async = true;
    script.addEventListener('load', () => {
      if (window.Stream) resolve(window.Stream);
      else reject(new Error('Stream SDK loaded but window.Stream missing'));
    });
    script.addEventListener('error', () => reject(new Error('Failed to load Stream SDK')));
    document.head.appendChild(script);
  });
}

export function EpisodePlayer({ episodeId, videoDurationSeconds, onProgress, onCompleted }: Props) {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Keeps the most recently reported watch time so we throttle DB writes to
  // ~once per 10s while the user is actively watching.
  const lastReportedRef = useRef<number>(0);

  const tokenQuery = useQuery({
    queryKey: ['playback', episodeId],
    queryFn: () =>
      api<PlaybackTokenResponse>(`/api/v1/episodes/${episodeId}/playback-token`, {
        method: 'POST',
      }),
    staleTime: 0,
    retry: false,
  });

  const token = tokenQuery.data;

  // Token rotation: silently re-fetch 60s before expiry so playback is
  // uninterrupted by token expiry mid-segment. Also re-fetch when the tab
  // becomes visible again — setTimeout pauses on backgrounded tabs and a stale
  // token would 401 the next segment request.
  useEffect(() => {
    if (!token) return;
    const msUntilRefresh =
      (token.expiresAt - Math.floor(Date.now() / 1000) - REFETCH_BEFORE_EXPIRY_SECONDS) * 1000;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (msUntilRefresh <= 0) {
      void tokenQuery.refetch();
    } else {
      timeout = setTimeout(() => void tokenQuery.refetch(), msUntilRefresh);
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'visible' && token) {
        const remaining = token.expiresAt - Math.floor(Date.now() / 1000);
        if (remaining < REFETCH_BEFORE_EXPIRY_SECONDS) {
          void tokenQuery.refetch();
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [token, tokenQuery]);

  // Attach to the Stream Player SDK once the iframe is mounted with a token.
  // We listen for timeupdate (throttled), pause, and ended to push progress to
  // our /progress endpoint. Auto-completion at 95% is computed server-side from
  // the watchedSeconds we send.
  useEffect(() => {
    if (!token || !iframeRef.current) return;
    let cancelled = false;
    let player: StreamPlayer | null = null;

    function reportProgress(rawSeconds: number) {
      const seconds = Math.max(0, Math.floor(rawSeconds));
      if (seconds <= lastReportedRef.current) return;
      lastReportedRef.current = seconds;
      onProgress?.(seconds);
      void api(`/api/v1/episodes/${episodeId}/progress`, {
        method: 'POST',
        body: { watchedSeconds: seconds },
      })
        .then((res) => {
          const r = res as { courseCompleted?: boolean; completed?: boolean };
          if (r?.completed) onCompleted?.();
        })
        .catch((err) => console.error('progress report failed', err));
    }

    function onTimeUpdate() {
      if (!player) return;
      const t = player.currentTime;
      // Throttle to one report per PROGRESS_REPORT_INTERVAL_SECONDS bucket.
      const bucket = Math.floor(t / PROGRESS_REPORT_INTERVAL_SECONDS);
      const lastBucket = Math.floor(lastReportedRef.current / PROGRESS_REPORT_INTERVAL_SECONDS);
      if (bucket > lastBucket) {
        reportProgress(t);
      }
      // Also fire if the user has exceeded the 95% completion threshold —
      // we don't want to wait another 10s bucket.
      if (videoDurationSeconds && t >= 0.95 * videoDurationSeconds && lastReportedRef.current < t) {
        reportProgress(t);
      }
    }
    function onPause() {
      if (player) reportProgress(player.currentTime);
    }
    function onEnded() {
      if (player) reportProgress(player.currentTime);
    }

    void loadStreamSdk()
      .then((Stream) => {
        if (cancelled || !iframeRef.current) return;
        player = Stream(iframeRef.current);
        player.addEventListener('timeupdate', onTimeUpdate);
        player.addEventListener('pause', onPause);
        player.addEventListener('ended', onEnded);
      })
      .catch((err) => console.error('Stream SDK load failed', err));

    return () => {
      cancelled = true;
      if (player) {
        player.removeEventListener('timeupdate', onTimeUpdate);
        player.removeEventListener('pause', onPause);
        player.removeEventListener('ended', onEnded);
      }
    };
  }, [token, episodeId, videoDurationSeconds, onProgress, onCompleted]);

  if (tokenQuery.isError) {
    return (
      <div className="relative w-full aspect-video flex items-center justify-center bg-gradient-to-br from-error/8 to-error/4">
        <Alert variant="error" className="max-w-md text-center">
          <span className="inline-flex items-center gap-2 justify-center">
            <Lock size={14} />
            {t('player.error_load')}
          </span>
        </Alert>
      </div>
    );
  }
  if (!token) {
    return (
      <div className="relative w-full aspect-video flex items-center justify-center bg-gradient-to-br from-primary-light/25 via-white/40 to-primary/10 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none animate-shimmer"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            backgroundSize: '200% 100%',
          }}
          aria-hidden
        />
        <div className="relative inline-flex items-center gap-3 rounded-pill bg-white/75 backdrop-blur-md border border-glass-border-strong px-4 py-2 shadow-glass-soft">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <span className="font-sans text-body-small text-text-secondary">
            {t('player.loading')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full aspect-video bg-black overflow-hidden"
      onContextMenu={(e) => e.preventDefault()}
    >
      <iframe
        ref={iframeRef}
        src={token.iframeUrl}
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        title="Lekcia"
      />
      <Watermark text={`${token.watermark.email} · ID: ${token.watermark.userId}`} />
    </div>
  );
}
